import { create } from 'zustand';
import type { Message, ModelLabel } from '@/types/session';

interface ChatState {
  leftMessages: Message[];
  rightMessages: Message[];
  isStreamingLeft: boolean;
  isStreamingRight: boolean;
  currentInput: string;
  error: string | null;
}

interface ChatActions {
  setCurrentInput: (text: string) => void;
  sendMessage: (content: string, target?: ModelLabel | 'both') => void;
  appendChunk: (model: ModelLabel, chunk: string, metadata?: { brainRegion?: string; stage?: string }) => void;
  finalizeStream: (model: ModelLabel, fullText: string) => void;
  applySavedMessageId: (model: ModelLabel, messageId: string) => void;
  setMessages: (messages: Message[]) => void;
  clearMessages: () => void;
  setError: (error: string | null) => void;
  setStreaming: (model: ModelLabel, streaming: boolean) => void;
}

type ChatStore = ChatState & ChatActions;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export const useChatStore = create<ChatStore>((set, get) => ({
  leftMessages: [],
  rightMessages: [],
  isStreamingLeft: false,
  isStreamingRight: false,
  currentInput: '',
  error: null,

  setCurrentInput: (text: string) => {
    set({ currentInput: text });
  },

  sendMessage: (content: string, target: ModelLabel | 'both' = 'both') => {
    const userMessage: Message = {
      message_id: generateId(),
      role: 'user',
      content,
      model_label: target === 'both' ? null : target,
      timestamp: new Date().toISOString(),
    };

    const shouldSendLeft = target === 'both' || target === 'left';
    const shouldSendRight = target === 'both' || target === 'right';

    set((state) => ({
      leftMessages: shouldSendLeft ? [...state.leftMessages, userMessage] : state.leftMessages,
      rightMessages: shouldSendRight ? [...state.rightMessages, userMessage] : state.rightMessages,
      currentInput: '',
      isStreamingLeft: shouldSendLeft ? true : state.isStreamingLeft,
      isStreamingRight: shouldSendRight ? true : state.isStreamingRight,
      error: null,
    }));

    set((state) => ({
      leftMessages: shouldSendLeft
        ? [
            ...state.leftMessages,
            {
              message_id: generateId(),
              role: 'assistant',
              content: '',
              model_label: 'left',
              timestamp: new Date().toISOString(),
              metadata: { isStreaming: true },
            },
          ]
        : state.leftMessages,
      rightMessages: shouldSendRight
        ? [
            ...state.rightMessages,
            {
              message_id: generateId(),
              role: 'assistant',
              content: '',
              model_label: 'right',
              timestamp: new Date().toISOString(),
              metadata: { isStreaming: true },
            },
          ]
        : state.rightMessages,
    }));
  },

  appendChunk: (model: ModelLabel, chunk: string, metadata?: { brainRegion?: string; stage?: string }) => {
    const key = model === 'left' ? 'leftMessages' : 'rightMessages';
    const messages = get()[key];
    if (messages.length === 0) return;

    // 找到最后一条 assistant 消息并追加内容
    const lastIndex = messages.length - 1;
    const lastMsg = messages[lastIndex];
    if (lastMsg.role !== 'assistant') return;

    const updatedMsg: Message = {
      ...lastMsg,
      content: lastMsg.content + chunk,
      metadata: {
        ...lastMsg.metadata,
        ...metadata,
        isStreaming: true,
      },
    };

    const updatedMessages = [...messages];
    updatedMessages[lastIndex] = updatedMsg;

    set({ [key]: updatedMessages } as unknown as Partial<ChatState>);
  },

  finalizeStream: (model: ModelLabel, fullText: string) => {
    const key = model === 'left' ? 'leftMessages' : 'rightMessages';
    const isStreamingKey = model === 'left' ? 'isStreamingLeft' : 'isStreamingRight';
    const messages = get()[key];
    if (messages.length === 0) return;

    const lastIndex = messages.length - 1;
    const lastMsg = messages[lastIndex];
    if (lastMsg.role !== 'assistant') return;

    const updatedMsg: Message = {
      ...lastMsg,
      content: fullText || lastMsg.content,
      metadata: {
        ...lastMsg.metadata,
        isStreaming: false,
      },
    };

    const updatedMessages = [...messages];
    updatedMessages[lastIndex] = updatedMsg;

    set({
      [key]: updatedMessages,
      [isStreamingKey]: false,
    } as unknown as Partial<ChatState>);
  },

  applySavedMessageId: (model: ModelLabel, messageId: string) => {
    const key = model === 'left' ? 'leftMessages' : 'rightMessages';
    const messages = get()[key];
    const lastAssistantIndex = [...messages]
      .reverse()
      .findIndex((msg) => msg.role === 'assistant' && msg.model_label === model);
    if (lastAssistantIndex < 0) return;
    const index = messages.length - 1 - lastAssistantIndex;
    const updatedMessages = [...messages];
    updatedMessages[index] = { ...updatedMessages[index], message_id: messageId };
    set({ [key]: updatedMessages } as unknown as Partial<ChatState>);
  },

  setMessages: (messages: Message[]) => {
    const leftMessages = messages.filter(
      (msg) => (msg.role === 'user' && !msg.model_label) || msg.model_label === 'left'
    );
    const rightMessages = messages.filter(
      (msg) => (msg.role === 'user' && !msg.model_label) || msg.model_label === 'right'
    );
    set({
      leftMessages,
      rightMessages,
      isStreamingLeft: false,
      isStreamingRight: false,
      error: null,
    });
  },

  clearMessages: () => {
    set({
      leftMessages: [],
      rightMessages: [],
      isStreamingLeft: false,
      isStreamingRight: false,
      error: null,
    });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  setStreaming: (model: ModelLabel, streaming: boolean) => {
    const key = model === 'left' ? 'isStreamingLeft' : 'isStreamingRight';
    set({ [key]: streaming } as unknown as Partial<ChatState>);
  },
}));
